import React, { useState, useEffect, useMemo } from 'react';
import { Card, Button, Modal, Form, Input, Select, Switch, Tag, Space, Popconfirm, message, Row, Col, Statistic, Typography, Divider, Progress, Badge, Empty, Tabs } from 'antd';
import {
  UserAddOutlined, EditOutlined, DeleteOutlined, ReloadOutlined,
  CheckCircleOutlined, CloseCircleOutlined, ThunderboltOutlined, CheckOutlined, CloseOutlined,
  ClockCircleOutlined, UserOutlined, CrownOutlined, SafetyCertificateOutlined,
  SearchOutlined, EyeOutlined, MailOutlined, FormOutlined, GlobalOutlined, DatabaseOutlined
} from '@ant-design/icons';
import { listUsers, createUser, updateUser, deleteUser, toggleUserActive, approveUser, rejectUser, getUserQuota, setUserQuota } from '../services/adminService';
import TokenQuotaWidget from '../components/TokenQuotaWidget';
import DataPermissionsModal from '../components/DataPermissionsModal';
import { useTheme } from '../contexts/ThemeContext';

const { Option } = Select;
const { Title, Text } = Typography;

/* ── Inline token quota section for a selected user ─── */
const UserQuotaSection = ({ user, onUpdate }) => {
  const [quotaData, setQuotaData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [newQuota, setNewQuota] = useState('');

  useEffect(() => {
    if (user?.id) fetchQuota();
  }, [user?.id]); // eslint-disable-line

  const fetchQuota = async () => {
    try {
      setLoading(true);
      const data = await getUserQuota(user.id);
      setQuotaData(data);
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  const handleSaveQuota = async () => {
    try {
      let value = null;
      if (newQuota === '-1' || newQuota.toLowerCase() === 'unlimited') {
        value = -1;
      } else if (newQuota !== '' && newQuota.toLowerCase() !== 'default') {
        value = parseInt(newQuota, 10);
        if (isNaN(value) || value < 0) {
          message.error('Enter a valid number, "unlimited", or leave empty for default');
          return;
        }
      }
      await setUserQuota(user.id, value);
      message.success('Quota updated');
      setEditing(false);
      fetchQuota();
      if (onUpdate) onUpdate();
    } catch { message.error('Failed to update quota'); }
  };

  const fmt = (n) => {
    if (n === null || n === undefined || n === -1) return 'Unlimited';
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
    return n.toLocaleString();
  };

  if (loading) return <Text type="secondary">Loading quota...</Text>;
  if (!quotaData) return <Text type="secondary">Quota data unavailable</Text>;

  const isUnlimited = quotaData.daily_quota === -1 || quotaData.is_unlimited;
  const pct = isUnlimited ? 0 : quotaData.daily_quota > 0 ? Math.round((quotaData.used_today / quotaData.daily_quota) * 100) : 0;
  const barColor = isUnlimited ? '#52c41a' : pct >= 90 ? '#ff4d4f' : pct >= 70 ? '#faad14' : '#1890ff';

  return (
    <Space direction="vertical" style={{ width: '100%' }} size={8}>
      <Row align="middle" gutter={8}>
        <Col flex="auto">
          <Text strong>AI Token Quota</Text>
        </Col>
        <Col>
          <TokenQuotaWidget quotaInfo={quotaData} size={32} strokeWidth={3} />
        </Col>
      </Row>
      <Divider style={{ margin: '4px 0' }} />
      <Row>
        <Col span={10}><Text type="secondary">Daily Limit:</Text></Col>
        <Col span={14}>
          <Text strong>{fmt(quotaData.daily_quota)}</Text>
          {quotaData.quota_source === 'system_default' && !isUnlimited && (
            <Text type="secondary" style={{ fontSize: 11 }}> (default)</Text>
          )}
        </Col>
      </Row>
      <Row>
        <Col span={10}><Text type="secondary">Used Today:</Text></Col>
        <Col span={14}><Text>{fmt(quotaData.used_today)}</Text></Col>
      </Row>
      <Row>
        <Col span={10}><Text type="secondary">Remaining:</Text></Col>
        <Col span={14}><Text style={{ color: barColor }}>{isUnlimited ? 'Unlimited' : fmt(quotaData.remaining)}</Text></Col>
      </Row>
      {!isUnlimited && (
        <div>
          <Progress percent={Math.min(pct, 100)} strokeColor={barColor} size="small" format={() => `${pct}%`} />
        </div>
      )}
      <Divider style={{ margin: '4px 0' }} />
      <Row>
        <Col span={10}><Text type="secondary">Set Quota:</Text></Col>
        <Col span={14}>
          {editing ? (
            <Space size="small">
              <Input
                size="small" style={{ width: 130 }}
                placeholder="e.g. 5000000, unlimited"
                value={newQuota}
                onChange={e => setNewQuota(e.target.value)}
                onPressEnter={handleSaveQuota}
              />
              <Button size="small" type="primary" onClick={handleSaveQuota}>Save</Button>
              <Button size="small" onClick={() => setEditing(false)}>Cancel</Button>
            </Space>
          ) : (
            <Space size="small">
              <Button size="small" icon={<EditOutlined />} onClick={() => {
                setNewQuota(user.daily_token_quota === null ? '' : user.daily_token_quota === -1 ? 'unlimited' : String(user.daily_token_quota));
                setEditing(true);
              }}>Change</Button>
              <Button size="small" onClick={() => handleSaveQuota()} title="Revert to role default">Default</Button>
            </Space>
          )}
        </Col>
      </Row>
    </Space>
  );
};

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [permsModalUser, setPermsModalUser] = useState(null);
  const [form] = Form.useForm();
  const { themeConfig } = useTheme();

  // Filter / search state
  const [searchText, setSearchText] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [approvalFilter, setApprovalFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('profile');

  // Quota cache: { [userId]: quotaInfo }
  const [quotaCache, setQuotaCache] = useState({});

  const loadUsers = async () => {
    setLoading(true);
    try {
      const data = await listUsers();
      setUsers(data);
    } catch (err) {
      message.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadUsers(); }, []);

  // Fetch quota for all users when list changes
  useEffect(() => {
    if (!users.length) return;
    users.forEach(u => {
      getUserQuota(u.id)
        .then(data => setQuotaCache(prev => ({ ...prev, [u.id]: data })))
        .catch(() => {});
    });
  }, [users]);

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setActiveTab('profile');
  };

  const handleCreate = () => {
    setEditingUser(null);
    form.resetFields();
    form.setFieldsValue({ role: 'User', is_active: true });
    setModalVisible(true);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    form.setFieldsValue({
      email: user.email,
      username: user.username,
      full_name: user.full_name,
      role: user.role,
      is_active: user.is_active,
    });
    setModalVisible(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editingUser) {
        await updateUser(editingUser.id, values);
        message.success('User updated');
      } else {
        await createUser(values);
        message.success('User created');
      }
      setModalVisible(false);
      loadUsers();
    } catch (err) {
      if (err.message) message.error(err.message);
    }
  };

  const handleDelete = async (userId) => {
    try {
      await deleteUser(userId);
      message.success('User deleted');
      if (selectedUser?.id === userId) setSelectedUser(null);
      loadUsers();
    } catch (err) { message.error(err.message); }
  };

  const handleToggleActive = async (userId) => {
    try {
      await toggleUserActive(userId);
      message.success('User status toggled');
      loadUsers();
    } catch (err) { message.error(err.message); }
  };

  const handleApprove = async (userId) => {
    try {
      await approveUser(userId);
      message.success('User approved — they have been emailed.');
      loadUsers();
    } catch (err) { message.error(err.message); }
  };

  const handleReject = async (userId) => {
    try {
      await rejectUser(userId);
      message.success('User rejected — they have been emailed.');
      loadUsers();
    } catch (err) { message.error(err.message); }
  };

  const approvalTag = (status) => {
    if (status === 'pending') return <Tag color="orange" icon={<ClockCircleOutlined />}>Pending</Tag>;
    if (status === 'rejected') return <Tag color="red" icon={<CloseCircleOutlined />}>Rejected</Tag>;
    return <Tag color="green" icon={<CheckCircleOutlined />}>Approved</Tag>;
  };

  /**
   * Combined account status chip — collapses approval_status + is_active
   * into a single tag so user cards stay compact.
   */
  const renderStatusTag = (user) => {
    const status = user.approval_status || 'approved';
    if (status === 'pending') {
      return <Tag color="orange" icon={<ClockCircleOutlined />} style={{ margin: 0 }}>Pending Approval</Tag>;
    }
    if (status === 'rejected') {
      return <Tag color="red" icon={<CloseCircleOutlined />} style={{ margin: 0 }}>Rejected</Tag>;
    }
    // Approved → reflect active/inactive
    return user.is_active
      ? <Tag color="green" icon={<CheckCircleOutlined />} style={{ margin: 0 }}>Active</Tag>
      : <Tag color="default" icon={<CloseCircleOutlined />} style={{ margin: 0 }}>Inactive</Tag>;
  };
  const pendingCount = users.filter(u => u.approval_status === 'pending').length;

  // ── Filtering ────────────────────────────────────────
  const filteredUsers = useMemo(() => users.filter(user => {
    const q = searchText.trim().toLowerCase();
    const matchesSearch = !q
      || (user.username && user.username.toLowerCase().includes(q))
      || (user.email && user.email.toLowerCase().includes(q))
      || (user.full_name && user.full_name.toLowerCase().includes(q));

    const matchesRole =
      roleFilter === 'all' ||
      (roleFilter === 'admin' && user.role === 'Admin') ||
      (roleFilter === 'creator' && user.role === 'Creator') ||
      (roleFilter === 'user' && user.role === 'User');

    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'active' && user.is_active) ||
      (statusFilter === 'inactive' && !user.is_active);

    const matchesApproval =
      approvalFilter === 'all' ||
      ((user.approval_status || 'approved') === approvalFilter);

    return matchesSearch && matchesRole && matchesStatus && matchesApproval;
  }), [users, searchText, roleFilter, statusFilter, approvalFilter]);

  const renderRoleTag = (user) => {
    if (user.role === 'Admin') return <Tag icon={<CrownOutlined />} color="gold">Admin</Tag>;
    if (user.role === 'Creator') return <Tag icon={<FormOutlined />} color="blue">Creator</Tag>;
    if (user.role === 'User') return <Tag icon={<UserOutlined />} color="green">User</Tag>;
    return <Tag>{user.role || 'User'}</Tag>;
  };

  /**
   * Render a compact data-access chip for a user row.
   *
   * - Admins             → "Full access" (green).
   * - Group              → group name (blue).
   * - Group + overrides  → group name + "+ override" tag (orange dashed).
   * - Custom (no group)  → "Custom" (orange).
   */
  const renderDataAccessChip = (user) => {
    if (user.is_superuser || user.role === 'Admin') {
      return <Tag color="green" icon={<SafetyCertificateOutlined />}>Full access</Tag>;
    }
    const mode = (user.data_access_mode || 'custom').toLowerCase();
    if (mode === 'custom') {
      return <Tag color="orange" icon={<DatabaseOutlined />}>Custom</Tag>;
    }
    const gName = user.data_access_group_name || 'Unassigned';
    return (
      <Space size={4}>
        <Tag color="blue" icon={<DatabaseOutlined />} style={{ margin: 0 }}>{gName}</Tag>
        {mode === 'group_with_overrides' && (
          <Tag color="orange" style={{ margin: 0, borderStyle: 'dashed' }}>+ override</Tag>
        )}
      </Space>
    );
  };

  const fmtTokens = (n) => {
    if (n === null || n === undefined || n === -1) return 'Unlimited';
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
    return n.toLocaleString();
  };

  const statCardStyle = (accent) => ({
    borderLeft: `4px solid ${accent}`,
    border: `1px solid ${themeConfig.border}`,
    borderRadius: 10,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    transition: 'all 0.3s ease',
    background: themeConfig.mode === 'dark' ? themeConfig.backgroundSecondary : '#ffffff',
  });

  const cardChromeStyle = {
    border: `1px solid ${themeConfig.border}`,
    borderRadius: 12,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
  };

  return (
    <div style={{ padding: 24, background: themeConfig.contentBg, minHeight: '100vh' }}>
      {/* Page Header */}
      <div style={{ marginBottom: 20 }}>
        <Row align="middle" justify="space-between">
          <Col>
            <Title
              level={3}
              style={{
                margin: 0,
                color: themeConfig.primary,
                fontWeight: 'bold',
                letterSpacing: 1,
              }}
            >
              USER MANAGEMENT
            </Title>
            <Text type="secondary" style={{ fontSize: 13 }}>
              Manage users, roles, approvals, and AI token quotas
            </Text>
          </Col>
        </Row>
      </div>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={5}>
          <Card size="small" style={statCardStyle(themeConfig.primary)} bodyStyle={{ padding: '12px 16px' }}>
            <Statistic
              title={<Text style={{ fontSize: 12 }}>Total Users</Text>}
              value={users.length}
              prefix={<UserOutlined style={{ color: themeConfig.primary, fontSize: 18 }} />}
              valueStyle={{ color: themeConfig.primary, fontSize: 24, fontWeight: 'bold' }}
            />
          </Card>
        </Col>
        <Col span={5}>
          <Card size="small" style={statCardStyle('#52c41a')} bodyStyle={{ padding: '12px 16px' }}>
            <Statistic
              title={<Text style={{ fontSize: 12 }}>Active</Text>}
              value={users.filter(u => u.is_active).length}
              prefix={<CheckCircleOutlined style={{ color: '#52c41a', fontSize: 18 }} />}
              valueStyle={{ color: '#52c41a', fontSize: 24, fontWeight: 'bold' }}
            />
          </Card>
        </Col>
        <Col span={5}>
          <Card size="small" style={statCardStyle('#cf1322')} bodyStyle={{ padding: '12px 16px' }}>
            <Statistic
              title={<Text style={{ fontSize: 12 }}>Admins</Text>}
              value={users.filter(u => u.role === 'Admin').length}
              prefix={<CrownOutlined style={{ color: '#cf1322', fontSize: 18 }} />}
              valueStyle={{ color: '#cf1322', fontSize: 24, fontWeight: 'bold' }}
            />
          </Card>
        </Col>
        <Col span={5}>
          <Card size="small" style={statCardStyle('#1890ff')} bodyStyle={{ padding: '12px 16px' }}>
            <Statistic
              title={<Text style={{ fontSize: 12 }}>Verified</Text>}
              value={users.filter(u => u.email_verified).length}
              prefix={<SafetyCertificateOutlined style={{ color: '#1890ff', fontSize: 18 }} />}
              valueStyle={{ color: '#1890ff', fontSize: 24, fontWeight: 'bold' }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small" style={statCardStyle(pendingCount > 0 ? '#fa8c16' : '#999')} bodyStyle={{ padding: '12px 16px' }}>
            <Statistic
              title={<Text style={{ fontSize: 12 }}>Pending Approval</Text>}
              value={pendingCount}
              prefix={<ClockCircleOutlined style={{ color: pendingCount > 0 ? '#fa8c16' : '#999', fontSize: 18 }} />}
              valueStyle={{ color: pendingCount > 0 ? '#fa8c16' : '#999', fontSize: 24, fontWeight: 'bold' }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={16}>
        {/* Left Column - User List (65%) */}
        <Col xs={24} lg={16}>
          <Card
            title={
              <Space>
                <UserOutlined style={{ color: themeConfig.primary }} />
                <span style={{ fontWeight: 600 }}>List</span>
                <Badge
                  count={filteredUsers.length}
                  showZero
                  style={{ backgroundColor: themeConfig.primary }}
                />
              </Space>
            }
            extra={
              <Space>
                <Button icon={<ReloadOutlined />} onClick={loadUsers}>Refresh</Button>
                <Button type="primary" icon={<UserAddOutlined />} onClick={handleCreate}>Create User</Button>
              </Space>
            }
            loading={loading}
            style={{
              ...cardChromeStyle,
              height: 'calc(100vh - 270px)',
              overflow: 'visible',
            }}
            bodyStyle={{ padding: 0, overflowY: 'auto', overflowX: 'hidden', height: 'calc(100% - 57px)' }}
          >
            {/* Filter Controls - Single Line Layout */}
            <div style={{
              padding: '12px 16px',
              borderBottom: `1px solid ${themeConfig.border}`,
              background: themeConfig.mode === 'dark' ? themeConfig.backgroundSecondary : '#fafafa',
              position: 'sticky',
              top: 0,
              zIndex: 2,
            }}>
              <Row gutter={8} align="middle">
                <Col flex="auto">
                  <Input
                    prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                    placeholder="Search by username, email, or full name..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    allowClear
                  />
                </Col>
                <Col style={{ minWidth: 130 }}>
                  <Select value={roleFilter} onChange={setRoleFilter} style={{ width: '100%' }} placeholder="Role">
                    <Option value="all">All Roles</Option>
                    <Option value="admin">Admin</Option>
                    <Option value="creator">Creator</Option>
                    <Option value="user">User</Option>
                  </Select>
                </Col>
                <Col style={{ minWidth: 130 }}>
                  <Select value={statusFilter} onChange={setStatusFilter} style={{ width: '100%' }} placeholder="Status">
                    <Option value="all">All Status</Option>
                    <Option value="active">Active</Option>
                    <Option value="inactive">Inactive</Option>
                  </Select>
                </Col>
                <Col style={{ minWidth: 140 }}>
                  <Select value={approvalFilter} onChange={setApprovalFilter} style={{ width: '100%' }} placeholder="Approval">
                    <Option value="all">All Approvals</Option>
                    <Option value="pending">Pending</Option>
                    <Option value="approved">Approved</Option>
                    <Option value="rejected">Rejected</Option>
                  </Select>
                </Col>
              </Row>
            </div>

            {/* User List Cards - compact rows */}
            <div style={{ padding: 10 }}>
              {filteredUsers.length === 0 ? (
                <Empty description="No users found" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: '40px 0' }} />
              ) : (
                <Space direction="vertical" style={{ width: '100%' }} size={6}>
                  {filteredUsers.map(user => {
                    const isSelected = selectedUser?.id === user.id;
                    const isPending = user.approval_status === 'pending';
                    const quota = quotaCache[user.id];
                    const isUnlimited = quota && (quota.daily_quota === -1 || quota.is_unlimited);
                    return (
                      <Card
                        key={user.id}
                        hoverable
                        onClick={() => handleSelectUser(user)}
                        style={{
                          cursor: 'pointer',
                          border: isSelected ? `2px solid ${themeConfig.primary}` : `1px solid ${themeConfig.border}`,
                          borderRadius: 8,
                          background: themeConfig.mode === 'dark' ? themeConfig.backgroundSecondary : '#ffffff',
                          boxShadow: isSelected ? '0 4px 10px rgba(0,0,0,0.14)' : '0 1px 3px rgba(0,0,0,0.06)',
                          transition: 'all 0.2s',
                          position: 'relative',
                          overflow: 'visible',
                        }}
                        bodyStyle={{ padding: '8px 12px' }}
                      >
                        {/* Left accent bar for selected user */}
                        {isSelected && (
                          <div style={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: 4,
                            background: themeConfig.primary,
                            borderTopLeftRadius: 6,
                            borderBottomLeftRadius: 6,
                          }} />
                        )}

                        <Row align="middle" gutter={12} wrap={false} style={{ width: '100%' }}>
                          {/* LEFT — Email (+ username/full name as secondary) */}
                          <Col flex="1 1 32%" style={{ minWidth: 0, overflow: 'hidden' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                              <Text
                                strong
                                style={{ fontSize: 13, minWidth: 0 }}
                                ellipsis={{ tooltip: user.email }}
                              >
                                <MailOutlined style={{ marginRight: 6, color: themeConfig.primary }} />
                                {user.email}
                              </Text>
                              <Text
                                type="secondary"
                                style={{ fontSize: 11, minWidth: 0 }}
                                ellipsis={{ tooltip: user.full_name || user.username }}
                              >
                                {user.username}{user.full_name ? ` · ${user.full_name}` : ''}
                              </Text>
                            </div>
                          </Col>

                          {/* CENTER — User type, combined status, data access group */}
                          <Col flex="1 1 38%" style={{ minWidth: 0 }}>
                            <Space size={6} wrap>
                              {renderRoleTag(user)}
                              {renderStatusTag(user)}
                              {!user.email_verified && (
                                <Tag icon={<MailOutlined />} color="orange" style={{ margin: 0 }}>Unverified</Tag>
                              )}
                              {renderDataAccessChip(user)}
                            </Space>
                          </Col>

                          {/* RIGHT — Quota + action buttons (unchanged) */}
                          {quota && (
                            <Col flex="none" onClick={(e) => e.stopPropagation()}>
                              <Space size={4} align="center">
                                <TokenQuotaWidget quotaInfo={quota} size={24} strokeWidth={2} />
                                <Text style={{ fontSize: 11, color: themeConfig.textSecondary, whiteSpace: 'nowrap' }}>
                                  {isUnlimited ? (
                                    <span style={{ color: '#52c41a', fontWeight: 600 }}>∞</span>
                                  ) : (
                                    <>
                                      {fmtTokens(quota.remaining)}
                                      <span style={{ opacity: 0.6 }}> / {fmtTokens(quota.daily_quota)}</span>
                                    </>
                                  )}
                                </Text>
                              </Space>
                            </Col>
                          )}

                          {/* Action buttons */}
                          <Col flex="none">
                            <Space size={0} onClick={(e) => e.stopPropagation()}>
                              {isPending && (
                                <>
                                  <Button
                                    type="text"
                                    size="small"
                                    icon={<CheckOutlined style={{ color: '#52c41a' }} />}
                                    title="Approve"
                                    onClick={(e) => { e.stopPropagation(); handleApprove(user.id); }}
                                  />
                                  <Popconfirm
                                    title="Reject this user?"
                                    onConfirm={(e) => { e?.stopPropagation?.(); handleReject(user.id); }}
                                  >
                                    <Button type="text" size="small" danger icon={<CloseOutlined />} title="Reject" />
                                  </Popconfirm>
                                </>
                              )}
                              <Button
                                type="text"
                                size="small"
                                icon={<EditOutlined />}
                                title="Edit User"
                                onClick={(e) => { e.stopPropagation(); handleEdit(user); }}
                              />
                              <Button
                                type="text"
                                size="small"
                                icon={<DatabaseOutlined />}
                                title="Data Access"
                                onClick={(e) => { e.stopPropagation(); setPermsModalUser(user); }}
                              />
                              <Popconfirm
                                title={`${user.is_active ? 'Deactivate' : 'Activate'} this user?`}
                                onConfirm={(e) => { e?.stopPropagation?.(); handleToggleActive(user.id); }}
                              >
                                <Button
                                  type="text"
                                  size="small"
                                  icon={user.is_active ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
                                  title={user.is_active ? 'Deactivate' : 'Activate'}
                                />
                              </Popconfirm>
                              <Popconfirm
                                title="Delete User"
                                description="Are you sure? This action cannot be undone."
                                okText="Yes, Delete"
                                cancelText="Cancel"
                                okButtonProps={{ danger: true }}
                                onConfirm={(e) => { e?.stopPropagation?.(); handleDelete(user.id); }}
                              >
                                <Button type="text" size="small" danger icon={<DeleteOutlined />} title="Delete" />
                              </Popconfirm>
                            </Space>
                          </Col>
                        </Row>
                      </Card>
                    );
                  })}
                </Space>
              )}
            </div>
          </Card>
        </Col>

        {/* Right Column - Preview (35%) - always visible */}
        <Col xs={24} lg={8}>
          <Card
            title={
              <Space>
                <EyeOutlined style={{ color: themeConfig.primary }} />
                <span style={{ fontWeight: 600 }}>Preview</span>
              </Space>
            }
            style={{
              ...cardChromeStyle,
              height: 'calc(100vh - 270px)',
              overflow: 'visible',
            }}
            bodyStyle={{ overflowY: 'auto', height: 'calc(100% - 57px)', padding: 0 }}
          >
            {!selectedUser ? (
              <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                <Empty
                  description={
                    <Space direction="vertical" size={4}>
                      <Text type="secondary" style={{ fontSize: 15 }}>Select a user to view details</Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>Click on any user from the list</Text>
                    </Space>
                  }
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  style={{ marginTop: 40 }}
                />
              </div>
            ) : (
              <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                style={{ margin: 16 }}
                items={[
                  {
                    key: 'profile',
                    label: <span><UserOutlined /> Profile</span>,
                    children: (
                      <div style={{ padding: '12px 4px' }}>
                        <div style={{ marginBottom: 16 }}>
                          <Tag color={themeConfig.primary} style={{ padding: '6px 14px', fontSize: 14, borderRadius: 4 }}>
                            <Space>
                              <UserOutlined />
                              {selectedUser.username}
                            </Space>
                          </Tag>
                        </div>

                        <Space direction="vertical" style={{ width: '100%' }} size={20}>
                          <div>
                            <Title level={5} style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                              <UserOutlined style={{ color: themeConfig.primary }} />
                              User Information
                            </Title>
                            <Space direction="vertical" style={{ width: '100%' }} size={8}>
                              <Row><Col span={10}><Text strong>Username:</Text></Col><Col span={14}><Text>{selectedUser.username}</Text></Col></Row>
                              <Divider style={{ margin: '4px 0' }} />
                              <Row><Col span={10}><Text strong>Email:</Text></Col><Col span={14}><Text copyable>{selectedUser.email}</Text></Col></Row>
                              <Divider style={{ margin: '4px 0' }} />
                              <Row><Col span={10}><Text strong>Full Name:</Text></Col><Col span={14}><Text>{selectedUser.full_name || <Text type="secondary">Not set</Text>}</Text></Col></Row>
                              <Divider style={{ margin: '4px 0' }} />
                              <Row>
                                <Col span={10}><Text strong>Timezone:</Text></Col>
                                <Col span={14}>
                                  <Space>
                                    <GlobalOutlined style={{ color: themeConfig.primary }} />
                                    <Text>{selectedUser.timezone || 'UTC'}</Text>
                                  </Space>
                                </Col>
                              </Row>
                              <Divider style={{ margin: '4px 0' }} />
                              <Row><Col span={10}><Text strong>Role:</Text></Col><Col span={14}>{renderRoleTag(selectedUser)}</Col></Row>
                              <Divider style={{ margin: '4px 0' }} />
                              <Row>
                                <Col span={10}><Text strong>Status:</Text></Col>
                                <Col span={14}>
                                  {selectedUser.is_active
                                    ? <Tag icon={<CheckCircleOutlined />} color="green">Active</Tag>
                                    : <Tag icon={<CloseCircleOutlined />} color="red">Inactive</Tag>}
                                </Col>
                              </Row>
                              <Divider style={{ margin: '4px 0' }} />
                              <Row>
                                <Col span={10}><Text strong>Verified:</Text></Col>
                                <Col span={14}>
                                  {selectedUser.email_verified
                                    ? <Tag icon={<SafetyCertificateOutlined />} color="blue">Verified</Tag>
                                    : <Tag icon={<MailOutlined />} color="orange">Unverified</Tag>}
                                </Col>
                              </Row>
                              <Divider style={{ margin: '4px 0' }} />
                              <Row><Col span={10}><Text strong>Approval:</Text></Col><Col span={14}>{approvalTag(selectedUser.approval_status)}</Col></Row>
                              {selectedUser.created_at && (
                                <>
                                  <Divider style={{ margin: '4px 0' }} />
                                  <Row>
                                    <Col span={10}><Text strong>Created:</Text></Col>
                                    <Col span={14}><Text style={{ fontSize: 13 }}>{new Date(selectedUser.created_at).toLocaleString()}</Text></Col>
                                  </Row>
                                </>
                              )}
                              {selectedUser.updated_at && (
                                <>
                                  <Divider style={{ margin: '4px 0' }} />
                                  <Row>
                                    <Col span={10}><Text strong>Last Updated:</Text></Col>
                                    <Col span={14}><Text style={{ fontSize: 13 }}>{new Date(selectedUser.updated_at).toLocaleString()}</Text></Col>
                                  </Row>
                                </>
                              )}
                            </Space>
                          </div>

                          {selectedUser.approval_status === 'pending' && (
                            <Space>
                              <Button type="primary" icon={<CheckOutlined />} onClick={() => handleApprove(selectedUser.id)}>Approve</Button>
                              <Popconfirm title="Reject this user?" onConfirm={() => handleReject(selectedUser.id)}>
                                <Button danger icon={<CloseOutlined />}>Reject</Button>
                              </Popconfirm>
                            </Space>
                          )}
                        </Space>
                      </div>
                    ),
                  },
                  {
                    key: 'quota',
                    label: <span><ThunderboltOutlined /> Token Quota</span>,
                    children: (
                      <div style={{ padding: '12px 4px' }}>
                        <Title level={5} style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                          <ThunderboltOutlined style={{ color: themeConfig.primary }} />
                          AI Token Quota
                        </Title>
                        <UserQuotaSection user={selectedUser} onUpdate={loadUsers} />
                      </div>
                    ),
                  },
                ]}
              />
            )}
          </Card>
        </Col>
      </Row>

      <Modal title={editingUser ? 'Edit User' : 'Create User'} open={modalVisible}
        onOk={handleSubmit} onCancel={() => setModalVisible(false)} okText={editingUser ? 'Save' : 'Create'}>
        <Form form={form} layout="vertical">
          <Form.Item name="email" label="Email" rules={[{ required: !editingUser, type: 'email', message: 'Valid email required' }]}>
            <Input disabled={!!editingUser} />
          </Form.Item>
          <Form.Item name="username" label="Username" rules={[{ required: !editingUser, message: 'Username required' }]}>
            <Input disabled={!!editingUser} />
          </Form.Item>
          {!editingUser && (
            <Form.Item name="password" label="Password" rules={[{ required: true, message: 'Password required' }]}>
              <Input.Password />
            </Form.Item>
          )}
          <Form.Item name="full_name" label="Full Name">
            <Input />
          </Form.Item>
          <Form.Item name="role" label="Role" rules={[{ required: true }]}>
            <Select options={[{ value: 'Admin', label: 'Admin' }, { value: 'Creator', label: 'Creator' }, { value: 'User', label: 'User' }]} />
          </Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <DataPermissionsModal
        open={!!permsModalUser}
        user={permsModalUser}
        onClose={() => setPermsModalUser(null)}
        onSaved={() => { /* nothing to refresh in user list */ }}
      />
    </div>
  );
}

export default UserManagement;
